import { ReactiveControlsValidator } from '../extensions/form-validator';
import {
  FormGroup,
  FormBuilder,
  ValidationErrors,
  FormControl,
  FormArray,
  AbstractControl
} from '@angular/forms';
import { ToastOptions, ToastData, ToastyService } from 'ng2-toasty';
import { CompanyService } from '../services/company.service';

export abstract class ReactiveFormsValidator {
  public abstract formGroup: FormGroup;
  public inputTypes: string[];
  public abstract initReactiveForms(): void;

  constructor(
    protected formBuilder: FormBuilder,
    protected toastyService: ToastyService
  ) {
    this.initReactiveForms();
    this.inputTypes = ['INPUT', 'SELECT', 'NGX-SELECT', 'APP-DATE', 'NG-SELECT'];
  }

  isValidControl(controlName): boolean {
    return ReactiveControlsValidator.validateFormControl(
      this.formGroup,
      controlName
    );
  }

  isValidControlWithNodesSon(
    controlName,
    subGroupName,
    fatherGroupName
  ): boolean {
    const fatherGroup: FormGroup = <FormGroup>(
      this.formGroup.get(fatherGroupName)
    );
    const subGroup: FormGroup = <FormGroup>fatherGroup.get(subGroupName);
    return ReactiveControlsValidator.validateFormControl(subGroup, controlName);
  }

  isValidControlWithSubGroup(controlName, subGroupName): boolean {
    const subGroup: FormGroup = <FormGroup>this.formGroup.get(subGroupName);
    return ReactiveControlsValidator.validateFormControl(subGroup, controlName);
  }

  isValidCustomFormGroup(controlName, formGroup): boolean {
    return ReactiveControlsValidator.validateFormControl(
      formGroup,
      controlName
    );
  }

  validadeAllFormGroup(): void {
    ReactiveControlsValidator.validadeAllFormGroup(this.formGroup);
  }

  validadeAllFormGroupWithMessage(): void {
    this.validadeAllFormGroup();
    const message = this.getFieldName();

    this.addToast({
      title: '',
      msg: message,
      showClose: true,
      timeout: 5000,
      theme: 'bootstrap',
      type: 'error',
      position: 'bottom-center',
      closeOther: true
    });
  }

  getFieldName(): string {
    let cont = 0;
    let message = '<br>';

    if (this.formGroup.get('confirmed')) {
      return 'Confirme a veracidade das informações fornecidas e dê aceite para continuar!';
    }

    const objectList = document.getElementsByClassName('ng-invalid');

    for (let i = 0; i <= objectList.length - 1; i++) {
      if (this.inputTypes.includes(objectList[i]['tagName'])) {
        cont++;

        if (message && message.includes(objectList[i]['dataset'].value)) {
          return (`Parece que você não preencheu tudo. Verifique o(s) campo(s):<br> ${message}`);
        }

        const dataset = objectList[i]['dataset'];
        const value = dataset.value ? dataset.value : '';

        message += value ? `Campo: ${value}<br> ` : '';

        if (objectList.length >= 3) {
          if (cont === 3) {
            return (`Parece que você não preencheu tudo. Verifique o(s) campo(s):<br> ${message}`);
          }
        } else {
          return (`Parece que você não preencheu tudo. Verifique o(s) campo(s):<br> ${message}`);
        }
      }
    }
    return (`Parece que você não preencheu tudo. Verifique o(s) campo(s):<br> ${message}`);
  }

  addToast(options) {
    if (options.closeOther) {
      this.toastyService.clearAll();
    }

    const toastOptions: ToastOptions = {
      title: options.title,
      msg: options.msg,
      showClose: options.showClose,
      timeout: options.timeout,
      theme: options.theme,
      onAdd: (toast: ToastData) => {
        /* added */
      },
      onRemove: (toast: ToastData) => {
        /* removed */
      }
    };

    switch (options.type) {
      case 'default':
        this.toastyService.default(toastOptions);
        break;
      case 'info':
        this.toastyService.info(toastOptions);
        break;
      case 'success':
        this.toastyService.success(toastOptions);
        break;
      case 'wait':
        this.toastyService.wait(toastOptions);
        break;
      case 'error':
        this.toastyService.error(toastOptions);
        break;
      case 'warning':
        this.toastyService.warning(toastOptions);
        break;
    }
  }

  isValidForm(): boolean {
    return this.formGroup.valid;
  }

  hasValueFormByControl(formControlName: string): boolean {
    return this.formGroup.get(formControlName).value ? true : false;
  }

  hasValueFormByFieldName(formControlName: string, fieldName: string): boolean {
    const formField = this.formGroup.get(formControlName);
    return formField.get(fieldName).value ? true : false;
  }

  isValidFormByControl(formControName): boolean {
    return this.formGroup.get(formControName).valid;
  }

  changeControlDisableStateExcept(fields: string[], disable: boolean) {
    this.disableControls(this.formGroup, fields, disable);
  }

  private disableControls(formGroup: FormGroup, fields: string[], disable) {
    Object.keys(formGroup.controls).forEach((field) => {
      if (fields.indexOf(field) < 0) {
        const control = formGroup.get(field);
        if (control instanceof FormControl) {
          disable ? control.disable() : control.enable();
        } else if (control instanceof FormGroup) {
          this.disableControls(control, fields, disable);
        }
      }
    });
  }

  getFormValidationErrors() {
    Object.keys(this.formGroup.controls).forEach((key) => {
      const controlErrors: ValidationErrors = this.formGroup.get(key).errors;
      if (controlErrors !== null) {
        Object.keys(controlErrors).forEach((keyError) => { });
      }
    });
  }

  executeSaveDefaultError(): (error: any) => void {
    return (error) => {
      this.addToast({
        title: '',
        msg:
          'Parece que houve um erro ao salvar seus dados. Por favor entre em contato com a nossa área técnica.',
        showClose: true,
        timeout: 5000,
        theme: 'bootstrap',
        type: 'error',
        position: 'bottom-center',
        closeOther: true
      });
    };
  }

  showFormControlName(name: string): boolean {
    if (this.formGroup) {
      const split = name.split('.');
      if (split.length > 1) {
        const lastControlName = split[split.length - 1];
        const firstControlName = split[0];
        let control: AbstractControl;
        for (const controlName of split) {
          control = controlName === firstControlName
            ? this.formGroup.get(controlName)
            : control.get(controlName);

          if (!control) {
            return false;
          }

          if (control instanceof FormArray) {
            control = control.controls[0];
            if (!control) {
              return false;
            }
          }

          if (controlName === lastControlName && control) {
            return true;
          }
        }
      } else {
        const controlName = split[0];
        const formControlName = this.formGroup.get(controlName);
        return formControlName ? true : false;
      }
    }

    return false;
  }
}
